import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from './components/Header';
import PhoneInputForm from './components/PhoneInputForm';
import CallHistoryList from './components/CallHistoryList';
import BulkRunPanel from './components/BulkRunPanel';
import { CallRecord, AppState, UserBaseline } from './types';
import { api } from './services/api';
import { PollingService } from './services/polling';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './App.css';
import { config } from './config';

// Main App component that uses authentication
function AppContent() {
  const { user, loading: authLoading, signInWithGoogle, signOut } = useAuth();

  const [state, setState] = useState<AppState>({
    calls: [],
    currentCall: null,
    isPolling: false,
    lastUpdate: new Date(),
  });
  const [isInitiatingCall, setIsInitiatingCall] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [language, setLanguage] = useState<'en' | 'tr'>('en');

  // Initialize polling service callbacks
  const onUpdate = useCallback((calls: CallRecord[]) => {
    setState(prev => ({
      ...prev,
      calls,
      lastUpdate: new Date(),
    }));
    setError(null); // Clear any previous errors
  }, []);

  const onError = useCallback((error: Error) => {
    console.error('Polling error:', error);
    setError('Failed to update call status. Retrying...');
  }, []);

  // Initialize polling service with memoization
  const pollingService = useMemo(() => new PollingService({
    interval: 3000, // 3 seconds as per spec
    onUpdate,
    onError,
  }), [onUpdate, onError]);

  // Load initial calls and start polling - only when authenticated
  useEffect(() => {
    if (!user) {
      // Stop polling if user logs out
      pollingService.stop();
      setState(prev => ({ ...prev, isPolling: false, calls: [] }));
      return;
    }

    const initializeApp = async () => {
      try {
        setError(null); // Clear any previous errors
        const calls = await api.getCalls();
        setState(prev => ({ ...prev, calls }));
        pollingService.start();
        setState(prev => ({ ...prev, isPolling: true }));

        if (config.debug) {
          console.log('✅ Supabase connection successful');
          console.log('📊 Loaded calls:', calls.length);
        }
      } catch (error) {
        console.error('❌ Failed to load initial calls:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        setError(`Failed to connect to Supabase. Please check your configuration. Error: ${errorMessage}`);

        if (config.debug) {
          console.error('Full error details:', error);
        }
      }
    };

    initializeApp();

    // Cleanup polling on unmount or user change
    return () => {
      pollingService.stop();
    };
  }, [user, pollingService]);

  // Handle call initiation
  const handleCallInitiate = async (phoneNumber: string, baseline?: UserBaseline, propertyName?: string, propertyUrl?: string) => {
    setIsInitiatingCall(true);
    setError(null);

    try {
      if (config.debug) {
        console.log('📞 Initiating call for:', phoneNumber);
        if (baseline) console.log('🧩 With baseline:', baseline);
      }

      const response = await api.initiateCall(phoneNumber, baseline, propertyName, propertyUrl, undefined, language);

      if (config.debug) {
        console.log('✅ Call initiated successfully:', response);
      }

      // Add the new call to the state immediately for better UX
      const newCall: CallRecord = {
        id: response.callId,
        phoneNumber,
        propertyName,
        propertyUrl,
        status: 'processing',
        timestamp: new Date(),
        baseline,
      };

      setState(prev => ({
        ...prev,
        calls: [newCall, ...prev.calls],
        currentCall: newCall,
      }));

      if (config.debug) {
        console.log('📝 Added new call to state:', newCall);
      }

    } catch (error) {
      console.error('❌ Failed to initiate call:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      let userFriendlyMessage = 'Failed to initiate call. Please try again.';

      if (errorMessage.includes('429') || /rate limit/i.test(errorMessage)) {
        userFriendlyMessage = 'You are being rate limited by VAPI. Please wait ~1 minute and try again.';
      } else if (errorMessage.includes('VAPI')) {
        userFriendlyMessage = 'Call service is temporarily unavailable. Please try again in a few minutes.';
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        userFriendlyMessage = 'Network connection issue. Please check your internet connection.';
      } else if (errorMessage.includes('401') || errorMessage.includes('403')) {
        userFriendlyMessage = 'Authentication error. Please check your Supabase configuration.';
      }

      setError(userFriendlyMessage);

      if (config.debug) {
        console.error('Full error details:', error);
      }
    } finally {
      setIsInitiatingCall(false);
    }
  };

  // Show loading spinner while checking authentication
  if (authLoading) {
    return (
      <div className="App">
        <div className="auth-container">
          <div className="loading">Loading...</div>
        </div>
        <style>{`
          .App { min-height: 100vh; padding: 1rem; background: var(--hb-bg); color: var(--hb-slate); }
          .auth-container { display: flex; justify-content: center; align-items: center; min-height: 80vh; }
          .loading { font-size: 1.2rem; }
        `}</style>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!user) {
    return (
      <div className="App">
        <div className="auth-container">
          <div className="auth-card">
            <h1>HalalBooking Call Agent</h1>
            <p>Sign in to access your hotel verification calls</p>
            <button
              onClick={signInWithGoogle}
              className="google-signin-btn"
            >
              Sign in with Google
            </button>
          </div>
        </div>
        <style>{`
          .App { min-height: 100vh; padding: 1rem; background: var(--hb-bg); color: var(--hb-slate); }
          .auth-container { display: flex; justify-content: center; align-items: center; min-height: 80vh; }
          .auth-card {
            background: white;
            padding: 2rem;
            border-radius: 8px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            text-align: center;
            max-width: 400px;
            width: 100%;
          }
          .auth-card h1 { margin-bottom: 1rem; color: var(--hb-slate); }
          .auth-card p { margin-bottom: 2rem; color: var(--hb-muted); }
          .google-signin-btn {
            background: #4285f4;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 4px;
            font-size: 1rem;
            cursor: pointer;
            transition: background-color 0.2s;
          }
          .google-signin-btn:hover { background: #3367d6; }
        `}</style>
      </div>
    );
  }

  // Main authenticated app
  return (
    <div className="App">
      <div className="container">
        <Header
          systemStatus={state.isPolling ? 'online' : 'offline'}
          pollingActive={state.isPolling}
          user={user}
          onSignOut={signOut}
        />

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div></div>
          <div>
            <label style={{ marginRight: '0.5rem' }}>Language</label>
            <select value={language} onChange={(e) => setLanguage((e.target.value as 'en' | 'tr') || 'en')} disabled={isInitiatingCall}>
              <option value="en">English</option>
              <option value="tr">Türkçe</option>
            </select>
          </div>
        </div>

        <PhoneInputForm
          onCallInitiate={handleCallInitiate}
          isProcessing={isInitiatingCall}
        />

        <BulkRunPanel />

        <CallHistoryList
          calls={state.calls}
          isLoading={!state.isPolling && state.calls.length === 0}
        />

        {state.isPolling && (
          <div className="status-footer">
            <span>Last updated: {state.lastUpdate.toLocaleTimeString()}</span>
          </div>
        )}
      </div>

      <style>{`
        * {
          box-sizing: border-box;
        }

        body {
          margin: 0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
            'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
            sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          background: var(--hb-bg);
          color: var(--hb-slate);
          line-height: 1.6;
        }

        .App {
          min-height: 100vh;
          padding: 1.5rem;
        }

        .container {
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .error-banner {
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
          color: #991b1b;
          padding: 1rem 1.5rem;
          border-radius: var(--hb-radius);
          margin-bottom: 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border: 1px solid #fecaca;
          box-shadow: var(--hb-shadow);
        }

        .error-banner button {
          background: none;
          border: none;
          font-size: 1.25rem;
          cursor: pointer;
          color: #dc2626;
          padding: 0.25rem;
          border-radius: 4px;
          transition: background-color 0.2s;
        }

        .error-banner button:hover {
          background: rgba(220, 38, 38, 0.1);
        }

        .status-footer {
          text-align: center;
          margin-top: 2rem;
          padding: 1.5rem;
          color: var(--hb-muted);
          font-size: 0.875rem;
          background: var(--hb-bg-secondary);
          border-radius: var(--hb-radius);
          border: 1px solid var(--hb-border);
          box-shadow: var(--hb-shadow);
        }
      `}</style>
    </div>
  );
}

// Wrapper component that provides authentication context
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
